//
// AddressReputation
//
// Full reputation breakdown shown inside the Reputation tab on the address
// page. Structured to give the user:
//   1. Big score number + tier + range (hero card)
//   2. Refresh button (rate-limited server-side, disabled while pending)
//   3. Warnings for penalty / unavailable factors, if applicable
//   4. Per-factor breakdown: label, description, weight bar, earned points
//   5. Timestamps (computed / last refresh)
//
// Reuses the ReputationBadge's data hook so a single mount = one network
// call regardless of how many components read from it.
//
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Heading,
  Progress,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';

import useReputationScore, { type ReputationFactor } from 'lib/api/useReputationScore';
import Skeleton from 'ui/shared/chakra/Skeleton';
import IconSvg from 'ui/shared/IconSvg';
import { getTierColorScheme, getTierRangeLabel } from 'ui/shared/reputation/ReputationTierColors';

// Human-readable representation of each factor's raw payload. Shows
// contextual data below the description so users see the numbers behind
// their score.
function formatRawSignal(factor: ReputationFactor): string | null {
  const raw = factor.raw as Record<string, unknown>;
  switch (factor.factor) {
    case 'kyc':
      return raw.kycVerified ? 'Verified' : 'Not verified';
    case 'id': {
      const idType = typeof raw.idType === 'string' ? raw.idType : 'unknown';
      const owns = Boolean(raw.ownsDomain);
      if (!owns) return 'No QIE ID owned';
      return `Owns a ${ idType } QIE ID`;
    }
    case 'age':
      return typeof raw.walletAge === 'string' ? `Wallet age: ${ raw.walletAge }` : null;
    case 'tx':
      return typeof raw.txCount === 'number' ? `${ raw.txCount.toLocaleString() } transactions` : null;
    case 'swaps':
      return typeof raw.swapCount === 'number' ? `${ raw.swapCount.toLocaleString() } swaps` : null;
    case 'holdings':
      return typeof raw.balance === 'number' ? `${ raw.balance.toLocaleString() } QIE` : null;
    case 'lock':
      if (typeof raw.lockAmount !== 'number') return null;
      return raw.lockAmount > 0 ? `${ raw.lockAmount.toLocaleString() } QIE locked` : 'Nothing locked';
    case 'staking':
      if (typeof raw.stakeAmount === 'number') {
        return raw.stakeAmount > 0 ?
          `${ raw.stakeAmount.toLocaleString() } QIE staked` :
          'Not staking';
      }
      return null;
    default:
      return null;
  }
}

function formatWhenRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const deltaMs = Date.now() - t;
  const min = Math.round(deltaMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${ min } minute${ min === 1 ? '' : 's' } ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${ hr } hour${ hr === 1 ? '' : 's' } ago`;
  const day = Math.round(hr / 24);
  return `${ day } day${ day === 1 ? '' : 's' } ago`;
}

interface Props {
  addressHash: string;
  // Consumers (like the tab wrapper) pass this so we skip work while the
  // wider page is still loading its own data.
  shouldRender?: boolean;
  isQueryEnabled?: boolean;
}

const AddressReputation = ({ addressHash, shouldRender = true, isQueryEnabled }: Props) => {
  const { data, isPending, isRefreshing, refresh, refreshError, clearRefreshError } =
    useReputationScore(addressHash, { enabled: isQueryEnabled !== false });

  const cardBg = useColorModeValue('white', 'whiteAlpha.100');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');

  if (!shouldRender) return null;

  if (isPending) {
    return (
      <VStack align="stretch" spacing={ 4 }>
        <Skeleton height="120px" borderRadius="md"/>
        <Skeleton height="200px" borderRadius="md"/>
      </VStack>
    );
  }

  if (!data) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon/>
        Reputation service is currently unavailable for this address.
      </Alert>
    );
  }

  const tierColor = getTierColorScheme(data.tier);
  const range = getTierRangeLabel(data.tier);

  return (
    <VStack align="stretch" spacing={ 6 }>
      { /* Hero card — big score + tier + refresh */ }
      <Box
        borderWidth="1px"
        borderColor={ cardBorder }
        borderRadius="lg"
        p={ 6 }
        bg={ cardBg }
      >
        <Flex justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={ 4 }>
          <Box>
            <Text fontSize="xs" textTransform="uppercase" letterSpacing="wider" variant="secondary" mb={ 2 }>
              QIE Reputation Score
            </Text>
            <HStack alignItems="baseline" spacing={ 3 }>
              <Heading as="span" size="2xl" color={ `${ tierColor }.500` }>
                { data.score.toFixed(2) }
              </Heading>
              <Text as="span" fontSize="lg" variant="secondary">/ 100</Text>
            </HStack>
            <HStack mt={ 3 } spacing={ 2 } flexWrap="wrap">
              <Box
                as="span"
                bg={ `${ tierColor }.500` }
                color="white"
                fontWeight={ 700 }
                fontSize="sm"
                px={ 3 }
                py={ 1 }
                borderRadius="md"
              >
                { data.tier }
              </Box>
              { range && (
                <Text as="span" fontSize="sm" variant="secondary">
                  Tier range { range }
                </Text>
              ) }
              { data.penalty > 0 && (
                <Box
                  as="span"
                  bg="red.500"
                  color="white"
                  fontSize="sm"
                  px={ 2 }
                  py={ 1 }
                  borderRadius="md"
                >
                  Penalty −{ data.penalty }
                </Box>
              ) }
            </HStack>
            <Text mt={ 4 } fontSize="xs" variant="secondary">
              Computed { formatWhenRelative(data.computedAt) } · Model { data.modelVersion }
            </Text>
          </Box>

          <VStack align="flex-end" spacing={ 2 }>
            <Button
              size="sm"
              variant="outline"
              onClick={ refresh }
              isLoading={ isRefreshing }
              loadingText="Refreshing"
              leftIcon={ <IconSvg name="repeat" boxSize={ 4 }/> }
              isDisabled={ isRefreshing }
            >
              Refresh score
            </Button>
            <Text fontSize="xs" variant="secondary" textAlign="right">
              Rate-limited to once per address every 5 min
            </Text>
          </VStack>
        </Flex>
      </Box>

      { /* Refresh error banner — dismissable */ }
      { refreshError && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon/>
          <Box flex={ 1 }>{ refreshError.message }</Box>
          <Button size="xs" variant="ghost" onClick={ clearRefreshError }>Dismiss</Button>
        </Alert>
      ) }

      { /* Warnings for partial data */ }
      { data.unavailableFactors.length > 0 && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon/>
          Some scoring signals were unavailable at compute time ({ data.unavailableFactors.join(', ') }).
          The current score may be understated.
        </Alert>
      ) }
      { data.authErrors.length > 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon/>
          Auth-gated factors ({ data.authErrors.join(', ') }) couldn&apos;t be verified without a signed-in session.
        </Alert>
      ) }

      { /* Penalty explainer */ }
      { data.penaltyDescription && (
        <Box
          borderWidth="1px"
          borderColor={ cardBorder }
          borderRadius="md"
          px={ 4 }
          py={ 3 }
          bg={ cardBg }
        >
          <Text fontSize="sm" variant="secondary">
            <Text as="span" fontWeight={ 600 }>About penalties: </Text>
            { data.penaltyDescription }
          </Text>
        </Box>
      ) }

      { /* Factor breakdown */ }
      <Box>
        <Heading size="sm" mb={ 4 }>Score breakdown</Heading>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={ 4 }>
          { data.factors.map((f) => (
            <FactorCard key={ f.factor } factor={ f } cardBg={ cardBg } cardBorder={ cardBorder }/>
          )) }
        </SimpleGrid>
      </Box>

      { /* Footer with cache info */ }
      <Divider/>
      <Text fontSize="xs" variant="secondary" textAlign="center">
        Last refreshed { formatWhenRelative(data.lastRefreshAt) }
        { data.cached ? ' (served from cache)' : '' }
      </Text>
    </VStack>
  );
};

interface FactorCardProps {
  factor: ReputationFactor;
  cardBg: string;
  cardBorder: string;
}

const FactorCard = ({ factor, cardBg, cardBorder }: FactorCardProps) => {
  const rawSignal = formatRawSignal(factor);
  const percent = factor.weight > 0 ? Math.round((factor.points / factor.weight) * 100) : 0;
  const earned = factor.points > 0;

  return (
    <Box
      borderWidth="1px"
      borderColor={ cardBorder }
      borderRadius="md"
      p={ 4 }
      bg={ cardBg }
    >
      <Flex justifyContent="space-between" alignItems="flex-start" mb={ 2 } gap={ 2 }>
        <Text fontWeight={ 600 } fontSize="sm">{ factor.label }</Text>
        <Tooltip label={ `Up to ${ factor.weight } points possible` } placement="top">
          <Text fontSize="sm" fontWeight={ 700 } color={ earned ? 'green.500' : 'gray.500' } whiteSpace="nowrap">
            { factor.points.toFixed(2) } / { factor.weight }
          </Text>
        </Tooltip>
      </Flex>
      <Progress
        value={ percent }
        size="xs"
        colorScheme={ earned ? 'green' : 'gray' }
        borderRadius="full"
        mb={ 3 }
      />
      <Text fontSize="xs" variant="secondary" mb={ rawSignal ? 2 : 0 }>
        { factor.description }
      </Text>
      { rawSignal && (
        <Text fontSize="xs" fontWeight={ 500 }>
          { rawSignal }
        </Text>
      ) }
    </Box>
  );
};

export default AddressReputation;